/**
 * A Service used to generate links for service resources
 */
export class LinkBuilder {
  private links = {
    customers: '/customers/',
    dashboardWidgets: '/dashboard-widgets/',
    documentTemplates: '/document-templates/',
    financialPeriods: '/financial-periods/',
    interimPaymentContracts: '/interim-payment-contracts/',
    interimPaymentRuleTemplates: '/interim-payment-rule-templates/',
    reimbursementRules: '/reimbursement-rules/',
    reimbursementRuleTemplates: '/reimbursement-rule-templates/',
    reimbursementStatetments: '/reimbursement-statements/',
    reportDatasetRequests: '/report-dataset-requests/',
    schemes: '/schemes/',
    transportOperators: '/transport-operators/',
    transportOperatorServices: '/transport-operator-services/',
    catalogues: '/catalogues',
    routes: '/routes',
    ticketTypes: '/ticket-types',
    paymentTokens: '/payment-tokens/',
    chargeAccounts: '/charge-accounts/',
    usagePatterns: '/usage-patterns/',
    productSettlementPeriod: '/product-settlement-periods/',
    transportLocations: '/transport-locations/',
    usageEventProductMappings: '/usage-event-product-mappings/',
    customerSchemas: '/customer-schemas/',
    usageEventCorrections: '/usage-event-corrections/',
    usageEvents: '/usage-events/',
    entitlements: '/entitlements/'
  };

  /**
* Gets a usage event link using an id
*
* @param id the id of the usage event
* @returns the usage event link
*/
  public getUsageEventLink = (id: string) => {
    return this.links.usageEvents + id;
  }

  /**
  * Gets a usage event correction link using an id
  *
  * @param id the id of the usage event correction
  * @returns the usage event correction link
  */
  public getUsageEventCorrectionLink = (id: string) => {
    return this.links.usageEventCorrections + id;
  }

  /**
  * Gets a customer schema link using an id
  *
  * @param id the id of the customer schema
  * @returns the customer schema link
  */
  public getCustomerSchemasLink = (id: string) => {
    return this.links.customerSchemas + id;
  }

  /**
  * Gets a transport location link using an id
  *
  * @param id the id of the transport location
  * @returns the transport location link
  */
  public getTransportLocationLink = (id: string) => {
    return this.links.transportLocations + id;
  }

  /**
  * Gets a usage pattern link using an id
  *
  * @param usagePatternId the id of the usage pattern
  * @returns the usage pattern link
  */
  public getUsagePatternLink = (usagePatternId: string) => {
    return this.links.usagePatterns + usagePatternId;
  }

  /**
  * Gets a usage event product mapping link using an id
  *
  * @param usageEventProductMappingId the id of the usage event product mapping
  * @returns the usage event product mapping link
  */
  public getUsageEventProductMappingLink = (usageEventProductMappingId: string) => {
    return this.links.usageEventProductMappings + usageEventProductMappingId;
  }

  /**
    * Gets a charge account link using a charge account id
    *
    * @param chargeAccountId the id of the charge account
    * @returns the charge account link
    */
  public getChargeAccountLink = (chargeAccountId: string) => {
    return this.links.chargeAccounts + chargeAccountId;
  }

  /**
   * Gets a payment token link using a payment token id
   *
   * @param paymentTokenId the id of the payment token
   * @returns the payment token link
   */
  public getPaymentTokenLink = (paymentTokenId: string) => {
    return this.links.paymentTokens + paymentTokenId;
  }

  /**
   * Gets a customers link using a customer id
   *
   * @param customerId the id of the customer
   * @returns the customers link
   */
  public getCustomersLink = (customerId: string) => {
    return this.links.customers + customerId;
  }

  /**
   * Gets a dashboard widgets link using a widget id
   *
   * @param widgetId the id of the widget
   * @returns the dashboard widgets link
   */
  public getDashboardWidgetsLink = (widgetId: string) => {
    return this.links.dashboardWidgets + widgetId;
  }

  /**
   * Gets a customers link using a customer id
   *
   * @param documentTemplateId the id of the document template
   * @returns the document templates link
   */
  public getDocumentTemplatesLink = (documentTemplateId: string) => {
    return this.links.documentTemplates + documentTemplateId;
  }

  /**
   * Gets a financial periods link using a period id
   *
   * @param financialPeriodId the id of the period
   * @returns the financial periods link
   */
  public getFinancialPeriodsLink = (financialPeriodId: string) => {
    return this.links.financialPeriods + financialPeriodId;
  }

  /**
   * Gets an interim payment contracts link using an interim contract id
   *
   * @param interimContractId the id of the interim contract
   * @returns the interim payment contracts link
   */
  public getInterimPaymentContractsLink = (interimContractId: string) => {
    return this.links.interimPaymentContracts + interimContractId;
  }

  /**
 * Gets a settlement period link using the supplied id
 *
 * @param productSettlementPeriodId the id of the product settlement period to get a link for
 * @returns the product settlement period link
 */
  public getProductSettlementPeriodLink = (productSettlementPeriodId: string) => {
    return this.links.productSettlementPeriod + productSettlementPeriodId;
  }

  /**
   * Gets an interim payment rule templates link using an interim payment rule template id
   *
   * @param interimPaymentRuleTemplateId the id of the interim payment rule template
   * @returns the interim payment rule templates link
   */
  public getInterimPaymentRuleTemplatesLink = (interimPaymentRuleTemplateId: string) => {
    return this.links.interimPaymentRuleTemplates + interimPaymentRuleTemplateId;
  }

  /**
   * Gets a reimbursement rules link using a reimbursement rule id
   *
   * @param reimbursementRuleId the id of the reimbursement rule
   * @returns the reimbursement rules link
   */
  public getReimbursementRulesLink = (reimbursementRuleId: string) => {
    return this.links.reimbursementRules + reimbursementRuleId;
  }

  /**
   * Gets a reimbursement rule templates link using a reimbursement rule template id
   *
   * @param reimbursementRuleTemplateId the id of the reimbursement rule template
   * @returns the reimbursement rule templates link
   */
  public getReimbursementRuleTemplatesLink = (reimbursementRuleTemplateId: string) => {
    return this.links.reimbursementRuleTemplates + reimbursementRuleTemplateId;
  }

  /**
   * Gets a reimbursement statements link using a reimbursement statement id
   *
   * @param reimbursementStatementId the id of the reimbursement statement
   * @returns the reimbursement statements link
   */
  public getReimbursementStatementsLink = (reimbursementStatementId: string) => {
    return this.links.reimbursementStatetments + reimbursementStatementId;
  }

  /**
   * Gets a report dataset requests link using a report dataset request id
   *
   * @param reportDatasetRequestId the id of the report dataset request
   * @returns the report dataset requests link
   */
  public getReportDatasetRequestsLink = (reportDatasetRequestId: string) => {
    return this.links.reportDatasetRequests + reportDatasetRequestId;
  }

  /**
   * Gets a schemes link using a scheme id
   *
   * @param schemeId the id of the scheme
   * @returns the schemes link
   */
  public getSchemesLink = (schemeId: string) => {
    return this.links.schemes + schemeId;
  }

  /**
   * Gets a transport operators link using a transport operator id
   *
   * @param transportOperatorId the id of the transport operator
   * @returns the transport operators link
   */
  public getTransportOperatorsLink = (transportOperatorId: string) => {
    return this.links.transportOperators + transportOperatorId;
  }

  /**
   * Gets a transport operator services link using a transport operator service id
   *
   * @param transportOperatorServiceId the id of the transport operator service
   * @returns the transport operator services link
   */
  public getTransportOperatorServicesLink = (transportOperatorServiceId: string) => {
    return this.links.transportOperatorServices + transportOperatorServiceId;
  }

  /**
   * Gets a product link
   *
   * @param options the productLink object
   * @returns the product link
   */
  public getProductLink = (catalogue: InterACT.Interfaces.Catalogues.Catalogue, product: InterACT.Interfaces.Catalogues.Product) => {
    return `${this.links.catalogues}/${catalogue.id}/products/${product.id}?catalogueVersion=${catalogue.version}`;
  }

  /**
   * Gets a route link using a route id
   *
   * @param route the route we wish to create a link for
   * @returns the route link
   */
  public getTicketTypeLink = (ticketTypeId: string) => {
    return `${this.links.ticketTypes}/${ticketTypeId}`;
  }

  /**
   * Gets an entitlement link using an entitlement id
   *
   * @param entitlementId the id of the entitlement
   * @returns the transport operators link
   */
  public getEntitlementsLink = (entitlementId: string) => {
    return this.links.entitlements + entitlementId;
  }
}
